import type { CSSProperties, ReactNode } from 'react';

export interface StatTileProps {
  /** Short noun phrase, sentence case. */
  label: string;
  /** Pre-formatted number; rendered with tabular figures, never rounded. */
  value: ReactNode;
  /** quiet = white card · standout = the blue panel, one per view · outline = a plain counter (no shadow). */
  variant?: 'quiet' | 'standout' | 'outline';
  /** A leading status mark (e.g. `<StatusMark tone="ok" />`): mark, label and number then sit on one row. */
  icon?: ReactNode;
  hint?: ReactNode;
  /** Blue ring while the tile is acting as an active table filter. */
  active?: boolean;
  /** Makes the tile a toggle button (`aria-pressed` follows `active`). */
  onClick?: () => void;
  style?: CSSProperties;
}

/** Summary number tile, laid out in an `.fs-tiles` grid. Interactive tiles render as toggle buttons. */
export function StatTile({ label, value, variant = 'quiet', icon, hint, active = false, onClick, style }: StatTileProps) {
  const classes = ['fs-tile'];
  if (variant !== 'quiet') classes.push(`fs-tile--${variant}`);
  if (icon) classes.push('fs-tile--marked');
  if (active) classes.push('fs-tile--active');
  const plain = typeof value === 'string' || typeof value === 'number';
  const theme = variant === 'standout' ? 'chrome' : undefined;
  const body = (
    <>
      {icon && (
        <span className="fs-tile__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="fs-tile__label">{label}</span>
      {/* Plain values get tabular figures here; composed values (number + unit) bring their own <Num>. */}
      <bdi className={plain ? 'fs-tile__value fs-num' : 'fs-tile__value'}>{value}</bdi>
      {hint && <span className="fs-tile__hint">{hint}</span>}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={classes.join(' ')} data-theme={theme} aria-pressed={active} onClick={onClick} style={style}>
        {body}
      </button>
    );
  }
  return (
    <div className={classes.join(' ')} data-theme={theme} style={style}>
      {body}
    </div>
  );
}
